<template>
  <div data-component="base-agent-search-options">
    <or-input
      :label="$t('hitl.modules.agent-picker.search-username-label')"
      :model-value="options.search || ''"
      :disabled="isDisabled"
      @update:modelValue="onPropertyChange({ search: $event })"
    />

    <or-select
      :label="$t('hitl.modules.agent-picker.search-status-label')"
      :model-value="options.status || []"
      :options="statusOptions"
      :multiple="true"
      :disabled="isDisabled"
      @update:modelValue="onPropertyChange({ status: $event })"
    />

    <or-select
      :label="$t('hitl.modules.agent-picker.search-rule-groups-label')"
      :model-value="options.ruleGroupIds || []"
      :options="ruleGroupsOptions"
      :multiple="true"
      :enable-search="true"
      :disabled="isDisabled"
      @update:modelValue="onPropertyChange({ ruleGroupIds: $event })"
    />

    <or-select
      :label="$t('hitl.modules.agent-picker.search-rule-tags-label')"
      :model-value="options.ruleTags || []"
      :options="ruleTagsOptions"
      :multiple="true"
      :enable-search="true"
      :disabled="isDisabled"
      @update:modelValue="onPropertyChange({ ruleTags: $event })"
    />
  </div>
</template>

<script lang="ts">
import _ from 'lodash';
import { defineComponent } from 'vue-demi';
import {
  OrInputV3 as OrInput,
  OrSelectV3 as OrSelect,
} from '@onereach/ui-components-25';

import { AGENT_STATUS_API, AGENT_STATUS_LABEL } from '@/constants';
import {
  AGENT_SEARCH_OPTIONS_PROP,
  RULE_GROUPS_PROP,
  STATUS_OPTIONS_PROP,
} from '../../constants';

/** We assume that custom statuses will also include default statuses like "available" or "busy" */
export default defineComponent({
  name: 'BaseAgentSearchOptions',
  components: {
    OrInput,
    OrSelect,
  },
  props: {
    options: AGENT_SEARCH_OPTIONS_PROP,
    statuses: STATUS_OPTIONS_PROP,
    ruleGroups: RULE_GROUPS_PROP,
    isDisabled: Boolean,
  },
  emits: ['options-change'],
  computed: {
    /** Lower-case values so we can compare them both to keys and statuses */
    statusOptions() {
      if (!this.statuses || !this.statuses.length) {
        return Object.values(AGENT_STATUS_API).map(status => {
          return {
            value: status.toLowerCase(),
            label: this.$t(AGENT_STATUS_LABEL[status]),
          };
        });
      }

      return this.statuses.map(status => {
        return {
          value: status.key,
          label: status.label || this.$t(AGENT_STATUS_LABEL[status.status]),
        };
      });
    },

    ruleGroupsOptions() {
      return this.ruleGroups.map(group => {
        return { value: group.groupId, label: group.name };
      });
    },

    ruleTagsOptions() {
      const RULE_SYMBOLS = ['|', '&', '!'];
      const rules = [];

      for (const group of this.ruleGroups) {
        rules.push(...group.rules);
      }

      const tags = rules.map(rule => {
        const tag = RULE_SYMBOLS.includes(rule[0]) ? rule.slice(1) : rule;
        return { value: tag, label: tag };
      });

      return _.uniqBy(tags, 'value');
    },
  },
  methods: {
    onPropertyChange(changes: object) {
      this.$emit('options-change', { ...this.options, ...changes });
    },
  },
});
</script>

<style lang="scss" scoped>
[data-component='base-agent-search-options'] {
  display: flex;
  flex-direction: column;
  gap: var(--s-4);
}
</style>
